import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { uploadCompressedPhoto } from "@/lib/imageUpload";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import {
  ArrowLeft, ArrowRight, Camera, CheckCircle2, ChevronRight, Package,
  Truck, Plus, Trash2, Upload, AlertTriangle, PenLine, Save, X, Image as ImageIcon,
  Barcode,
} from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";

const CONDITIONS = [
  { value: "good", label: "Good" },
  { value: "minor_damage", label: "Minor Damage" },
  { value: "damaged", label: "Damaged" },
  { value: "wet", label: "Wet" },
  { value: "crushed", label: "Crushed" },
  { value: "opened_tampered", label: "Opened / Tampered" },
  { value: "other", label: "Other" },
];

const ISSUE_TYPES = [
  { value: "damaged_pallet", label: "Damaged Pallet" },
  { value: "damaged_boxes", label: "Damaged Boxes" },
  { value: "wet_boxes", label: "Wet Boxes" },
  { value: "crushed_boxes", label: "Crushed Boxes" },
  { value: "opened_tampered_boxes", label: "Opened / Tampered Boxes" },
  { value: "missing_boxes", label: "Missing Boxes" },
  { value: "extra_boxes", label: "Extra Boxes" },
  { value: "pallet_count_mismatch", label: "Pallet Count Mismatch" },
  { value: "incorrect_shipment", label: "Incorrect Shipment" },
  { value: "delivery_note_mismatch", label: "Delivery Note Mismatch" },
  { value: "other", label: "Other" },
];

const STEPS = ["shipment", "counting", "delivery_note", "issues", "review"];
const STEP_LABEL = {
  shipment: "Shipment", counting: "Counting", delivery_note: "Delivery Note",
  issues: "Issues", review: "Review & Sign",
};

const SHIPMENT_TYPES = [
  { value: "pallets", label: "Pallets", icon: "📦", desc: "Boxes stacked on wooden pallets" },
  { value: "boxes", label: "Boxes", icon: "🧾", desc: "Loose cartons only, no pallets" },
  { value: "pallets_and_boxes", label: "Pallets + Boxes", icon: "🚛", desc: "Pallets plus additional loose boxes" },
];

function toLocalInputValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  } catch { return ""; }
}

export default function ReceivingNew() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [step, setStep] = useState("shipment");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [addSupOpen, setAddSupOpen] = useState(false);
  const [newSup, setNewSup] = useState({ name: "", code: "", contact_name: "", phone: "", email: "", address: "" });
  const [confirmSign, setConfirmSign] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = useCallback(() => {
    api.get(`/inventory_receivings/${id}`).then(({ data }) => setRec(data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/suppliers?active_only=true").then(({ data }) => setSuppliers(data)).catch(() => {});
  }, [addSupOpen]);

  const patch = useCallback(async (fields, opts = {}) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/inventory_receivings/${id}`, fields);
      setRec(data);
      setSavedAt(new Date());
      return data;
    } catch (e) {
      if (!opts.silent) toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }, [id]);

  // Debounced text saves
  const debounceRef = useRef({});
  const debouncedPatch = (key, fields) => {
    clearTimeout(debounceRef.current[key]);
    debounceRef.current[key] = setTimeout(() => patch(fields, { silent: true }), 500);
  };

  if (!rec) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  const locked = ["accepted", "accepted_with_issues", "cancelled"].includes(rec.status);
  if (locked) {
    // Redirect to detail
    return (
      <div className="hos-card p-6 text-center">
        This receiving is already {rec.status}.
        <div className="mt-4"><Link to={`/receiving/${rec.id}`} className="hos-btn-primary inline-flex items-center gap-2">View report</Link></div>
      </div>
    );
  }

  const setField = (k, v) => setRec({ ...rec, [k]: v });

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-24" data-testid="receiving-new-page">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Receiving · {rec.reference_number}</div>
          <h1 className="font-heading text-3xl">Receive Shipment</h1>
          <div className="text-xs text-[color:var(--text-secondary)] mt-1">Store: {rec.store_name} · Received by: {rec.received_by_name}</div>
        </div>
        <div className="text-xs text-[color:var(--text-secondary)] flex items-center gap-2">
          {saving ? <><Save size={12} className="animate-pulse"/> Saving…</> : savedAt ? <><CheckCircle2 size={12} className="text-emerald-400"/> Saved</> : null}
        </div>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(s)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs border ${step === s ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                  data-testid={`step-${s}`}>
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] ${step === s ? "bg-[color:var(--gold)] text-black" : "bg-[color:var(--bg-surface-2)]"}`}>{i + 1}</span>
            {STEP_LABEL[s]}
          </button>
        ))}
      </div>

      {step === "shipment" && (
        <ShipmentStep rec={rec} suppliers={suppliers} setField={setField} debouncedPatch={debouncedPatch} patch={patch}
                      onAddSupplier={() => setAddSupOpen(true)}/>
      )}
      {step === "counting" && <CountingStep rec={rec} setRec={setRec} patch={patch}/>}
      {step === "delivery_note" && <DeliveryNoteStep rec={rec} patch={patch}/>}
      {step === "issues" && <IssuesStep rec={rec} setRec={setRec} patch={patch}/>}
      {step === "review" && (
        <ReviewStep rec={rec} onSign={async () => {
          if (!confirmSign) return toast.error("Please tick the confirmation box.");
          setSigning(true);
          try {
            const { data } = await api.post(`/inventory_receivings/${rec.id}/accept`, { confirm: true });
            toast.success(`${data.reference_number} accepted`);
            nav(`/receiving/${rec.id}`, { state: { justAccepted: true } });
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setSigning(false); }
        }} confirmSign={confirmSign} setConfirmSign={setConfirmSign} signing={signing} user={user}/>
      )}

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-[color:var(--border)] bg-[color:var(--bg-surface)]/95 backdrop-blur-md p-3 z-30">
        <div className="max-w-4xl mx-auto flex justify-between gap-2">
          <button onClick={() => {
            const i = STEPS.indexOf(step);
            if (i > 0) setStep(STEPS[i - 1]);
          }} disabled={step === STEPS[0]} className="hos-btn-outline disabled:opacity-40" data-testid="prev-step-btn">
            ← Back
          </button>
          {step !== "review" && (
            <button onClick={() => {
              const i = STEPS.indexOf(step);
              setStep(STEPS[i + 1]);
            }} className="hos-btn-primary flex items-center gap-2" data-testid="next-step-btn">
              Next <ChevronRight size={14}/>
            </button>
          )}
        </div>
      </div>

      {/* Add Supplier Modal */}
      {addSupOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="hos-card w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-2xl">Add Supplier</h3>
              <button onClick={() => setAddSupOpen(false)} className="p-1"><X size={16}/></button>
            </div>
            <div className="space-y-2">
              <input className="hos-input" placeholder="Supplier Name *" value={newSup.name} onChange={(e) => setNewSup({ ...newSup, name: e.target.value })} data-testid="new-sup-name"/>
              <input className="hos-input" placeholder="Supplier Code" value={newSup.code} onChange={(e) => setNewSup({ ...newSup, code: e.target.value })}/>
              <input className="hos-input" placeholder="Contact Name" value={newSup.contact_name} onChange={(e) => setNewSup({ ...newSup, contact_name: e.target.value })}/>
              <input className="hos-input" placeholder="Phone" value={newSup.phone} onChange={(e) => setNewSup({ ...newSup, phone: e.target.value })}/>
              <input className="hos-input" placeholder="Email" value={newSup.email} onChange={(e) => setNewSup({ ...newSup, email: e.target.value })}/>
              <input className="hos-input" placeholder="Address" value={newSup.address} onChange={(e) => setNewSup({ ...newSup, address: e.target.value })}/>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="hos-btn-outline" onClick={() => setAddSupOpen(false)}>Cancel</button>
              <button className="hos-btn-primary" data-testid="new-sup-save" onClick={async () => {
                if (!newSup.name.trim()) return toast.error("Supplier name required");
                try {
                  const { data } = await api.post("/suppliers", { ...newSup, active: true });
                  toast.success("Supplier added");
                  setAddSupOpen(false);
                  setNewSup({ name: "", code: "", contact_name: "", phone: "", email: "", address: "" });
                  await patch({ supplier_id: data.id });
                } catch (e) { toast.error(formatApiError(e)); }
              }}>Save & Select</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 1: Shipment ---------- */
function ShipmentStep({ rec, suppliers, setField, debouncedPatch, patch, onAddSupplier }) {
  const currentType = rec.shipment_type || "pallets";
  return (
    <div className="space-y-4">
      <div className="hos-card p-6 space-y-3">
        <div>
          <div className="label-caps">How did this shipment arrive?</div>
          <div className="text-sm text-[color:var(--text-secondary)]">Choose the format so we count it correctly.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SHIPMENT_TYPES.map((t) => (
            <button key={t.value} type="button"
                    onClick={() => patch({ shipment_type: t.value })}
                    className={`p-4 rounded-md border-2 text-left transition ${currentType === t.value ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-[color:var(--border)] hover:border-[color:var(--gold)]/60"}`}
                    data-testid={`shipment-type-${t.value}`}>
              <div className="text-3xl">{t.icon}</div>
              <div className={`font-heading text-lg mt-1 ${currentType === t.value ? "text-[color:var(--gold)]" : ""}`}>{t.label}</div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-1">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="hos-card p-6 space-y-4">
      <div>
        <label className="label-caps">Supplier *</label>
        <div className="flex gap-2 mt-1">
          <select className="hos-input flex-1" value={rec.supplier_id || ""} data-testid="supplier-select"
                  onChange={(e) => patch({ supplier_id: e.target.value || null })}>
            <option value="">— select supplier —</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="hos-btn-outline flex items-center gap-1" onClick={onAddSupplier} data-testid="add-supplier-btn"><Plus size={14}/> Add</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label-caps">Purchase Order Number</label>
          <input className="hos-input mt-1" placeholder="PO-28471" value={rec.purchase_order_number || ""}
                 data-testid="po-input"
                 onChange={(e) => { setField("purchase_order_number", e.target.value); debouncedPatch("po", { purchase_order_number: e.target.value }); }}/>
        </div>
        <div>
          <label className="label-caps">Invoice Number</label>
          <input className="hos-input mt-1" placeholder="INV-98432" value={rec.invoice_number || ""}
                 data-testid="invoice-input"
                 onChange={(e) => { setField("invoice_number", e.target.value); debouncedPatch("inv", { invoice_number: e.target.value }); }}/>
        </div>
      </div>

      <div>
        <label className="label-caps">Arrival Date &amp; Time</label>
        <input type="datetime-local" className="hos-input mt-1" value={toLocalInputValue(rec.arrival_at)}
               data-testid="arrival-input"
               onChange={(e) => {
                 const val = e.target.value ? new Date(e.target.value).toISOString() : rec.system_created_at;
                 setField("arrival_at", val);
                 debouncedPatch("arrival", { arrival_at: val });
               }}/>
        <div className="text-[11px] text-[color:var(--text-secondary)] mt-1">System recorded: {new Date(rec.system_created_at).toLocaleString()}{rec.arrival_edited ? " · edited" : ""}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label-caps">Delivery Driver Name</label>
          <input className="hos-input mt-1" placeholder="Michael Smith" value={rec.driver_name || ""}
                 data-testid="driver-input"
                 onChange={(e) => { setField("driver_name", e.target.value); debouncedPatch("driver", { driver_name: e.target.value }); }}/>
        </div>
        <div>
          <label className="label-caps">Driver Phone</label>
          <input className="hos-input mt-1" placeholder="(917) 555-0142" value={rec.driver_phone || ""}
                 onChange={(e) => { setField("driver_phone", e.target.value); debouncedPatch("phone", { driver_phone: e.target.value }); }}/>
        </div>
        <div>
          <label className="label-caps">Carrier / Delivery Company</label>
          <input className="hos-input mt-1" placeholder="ABC Logistics" value={rec.carrier_name || ""}
                 data-testid="carrier-input"
                 onChange={(e) => { setField("carrier_name", e.target.value); debouncedPatch("carrier", { carrier_name: e.target.value }); }}/>
        </div>
        <div>
          <label className="label-caps">Truck / Vehicle Number</label>
          <input className="hos-input mt-1" value={rec.truck_number || ""}
                 onChange={(e) => { setField("truck_number", e.target.value); debouncedPatch("truck", { truck_number: e.target.value }); }}/>
        </div>
        <div>
          <label className="label-caps">Trailer Number</label>
          <input className="hos-input mt-1" value={rec.trailer_number || ""}
                 onChange={(e) => { setField("trailer_number", e.target.value); debouncedPatch("trailer", { trailer_number: e.target.value }); }}/>
        </div>
        <div>
          <label className="label-caps">Delivery Reference #</label>
          <input className="hos-input mt-1" value={rec.delivery_reference || ""}
                 onChange={(e) => { setField("delivery_reference", e.target.value); debouncedPatch("delivery_ref", { delivery_reference: e.target.value }); }}/>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-caps">Expected Pallets (optional)</label>
          <input type="number" min="0" inputMode="numeric" className="hos-input mt-1" value={rec.expected_pallets ?? ""}
                 onChange={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); setField("expected_pallets", v); debouncedPatch("exp_p", { expected_pallets: v }); }}/>
        </div>
        <div>
          <label className="label-caps">Expected Boxes (optional)</label>
          <input type="number" min="0" inputMode="numeric" className="hos-input mt-1" value={rec.expected_boxes ?? ""}
                 onChange={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); setField("expected_boxes", v); debouncedPatch("exp_b", { expected_boxes: v }); }}/>
        </div>
      </div>
      </div>
    </div>
  );
}

/* ---------- Step 2: Counting ---------- */
function CountingStep({ rec, setRec, patch }) {
  const [initInput, setInitInput] = useState("");
  const pallets = rec.pallets || [];
  const shipmentType = rec.shipment_type || "pallets";
  const showPallets = shipmentType === "pallets" || shipmentType === "pallets_and_boxes";
  const showLoose = shipmentType === "boxes" || shipmentType === "pallets_and_boxes";
  const looseBoxes = Number(rec.loose_boxes || 0);

  const initialize = async () => {
    const n = Math.max(1, Math.min(200, Number(initInput) || 0));
    if (!n) return toast.error("Enter total pallets to create");
    const newPallets = Array.from({ length: n }, (_, i) => ({ pallet_number: i + 1, box_count: 0, condition: "good", notes: "", photos: [], barcode: "" }));
    const data = await patch({ pallets: newPallets });
    if (data) setInitInput("");
  };
  const savePallets = async (arr) => patch({ pallets: arr });

  const addPallet = async () => {
    const arr = [...pallets, { pallet_number: pallets.length + 1, box_count: 0, condition: "good", notes: "", photos: [], barcode: "" }];
    await savePallets(arr);
  };
  const removePallet = async (idx) => {
    const p = pallets[idx];
    if ((p.box_count > 0 || (p.photos || []).length > 0) && !window.confirm(`Remove Pallet ${p.pallet_number}? Data will be lost.`)) return;
    const arr = pallets.filter((_, i) => i !== idx);
    await savePallets(arr);
  };
  const updatePallet = async (idx, patchP) => {
    const arr = pallets.map((p, i) => i === idx ? { ...p, ...patchP } : p);
    await savePallets(arr);
  };

  const completed = pallets.filter((p) => p.box_count > 0 || p.condition !== "good").length;
  const boxesOnPallets = pallets.reduce((s, p) => s + (Number(p.box_count) || 0), 0);
  const totalBoxes = (showPallets ? boxesOnPallets : 0) + (showLoose ? looseBoxes : 0);
  const pct = pallets.length ? Math.round(100 * completed / pallets.length) : 0;

  return (
    <div className="space-y-4">
      {/* Live counting summary */}
      <div className="hos-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Shipment" value={SHIPMENT_TYPES.find((t) => t.value === shipmentType)?.label}/>
        {showPallets && <MiniStat label="Pallets" value={pallets.length}/>}
        {showPallets && <MiniStat label="Boxes on Pallets" value={boxesOnPallets}/>}
        {showLoose && <MiniStat label="Loose Boxes" value={looseBoxes}/>}
        <MiniStat label="Total Boxes" value={totalBoxes} accent/>
      </div>

      {showPallets && pallets.length === 0 && (
        <div className="hos-card p-6">
          <div className="label-caps">Pallets Received</div>
          <div className="font-heading text-2xl mt-1">How many pallets did you receive?</div>
          <div className="flex gap-2 mt-4">
            <input type="number" min="1" max="200" inputMode="numeric"
                   className="hos-input text-4xl font-heading text-center w-40" value={initInput}
                   onChange={(e) => setInitInput(e.target.value)} data-testid="pallet-count-input"/>
            <button className="hos-btn-primary" onClick={initialize} data-testid="create-pallets-btn">Create Pallets</button>
          </div>
        </div>
      )}

      {showPallets && pallets.length > 0 && (
        <>
          <div className="hos-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="label-caps">Pallet Progress</div>
                <div className="font-heading text-xl">{completed} of {pallets.length} completed</div>
              </div>
              <button className="hos-btn-outline flex items-center gap-1 text-sm" onClick={addPallet} data-testid="add-pallet-btn"><Plus size={14}/> Add Pallet</button>
            </div>
            <div className="h-2 bg-[color:var(--bg-surface-2)] rounded-full overflow-hidden">
              <div className="h-full bg-[color:var(--gold)] transition-all" style={{ width: `${pct}%` }}/>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pallets.map((p, idx) => (
              <PalletCard key={p.id || idx} p={p} idx={idx} onChange={updatePallet} onRemove={() => removePallet(idx)}/>
            ))}
          </div>
        </>
      )}

      {showLoose && (
        <div className="hos-card p-6">
          <div className="label-caps">Loose Boxes / Cartons</div>
          <div className="font-heading text-2xl mt-1">
            {shipmentType === "boxes" ? "How many boxes did you receive?" : "How many loose boxes (not on a pallet)?"}
          </div>
          <input type="number" min="0" inputMode="numeric"
                 className="hos-input text-4xl font-heading text-center w-48 mt-4"
                 value={rec.loose_boxes ?? 0}
                 onChange={(e) => patch({ loose_boxes: Math.max(0, Number(e.target.value) || 0) })}
                 data-testid="loose-boxes-input"/>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="rounded-md border border-[color:var(--border)] p-3">
      <div className="label-caps text-[10px]">{label}</div>
      <div className={`font-heading text-2xl ${accent ? "text-[color:var(--gold)]" : ""}`}>{value ?? 0}</div>
    </div>
  );
}

function PalletCard({ p, idx, onChange, onRemove }) {
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const takePhoto = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { id: fid, url } = await uploadCompressedPhoto(file);
      onChange(idx, { photos: [...(p.photos || []), { id: fid, file_id: fid, url }] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const removePhoto = (i) => onChange(idx, { photos: (p.photos || []).filter((_, j) => j !== i) });

  const badge = p.condition === "good" ? "text-emerald-300 border-emerald-400/40" : "text-[color:var(--warning)] border-[color:var(--warning)]/40";
  return (
    <div className="hos-card p-4 space-y-3" data-testid={`pallet-card-${p.pallet_number}`}>
      <div className="flex items-center justify-between">
        <div className="font-heading text-lg">Pallet {p.pallet_number}</div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-1 rounded-full border ${badge}`}>{CONDITIONS.find((c) => c.value === p.condition)?.label || p.condition}</span>
          <button className="text-[color:var(--error)] p-1" onClick={onRemove}><Trash2 size={14}/></button>
        </div>
      </div>
      <div>
        <label className="label-caps">Boxes on Pallet</label>
        <input type="number" min="0" inputMode="numeric" className="hos-input mt-1 text-3xl font-heading"
               value={p.box_count} onChange={(e) => onChange(idx, { box_count: Math.max(0, Number(e.target.value) || 0) })}
               data-testid={`boxes-input-${p.pallet_number}`}/>
      </div>
      <div>
        <label className="label-caps">Condition</label>
        <div className="grid grid-cols-3 gap-1 mt-1">
          {CONDITIONS.map((c) => (
            <button key={c.value} type="button"
                    onClick={() => onChange(idx, { condition: c.value })}
                    className={`px-2 py-2 rounded-md text-xs border ${p.condition === c.value ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                    data-testid={`cond-${p.pallet_number}-${c.value}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="label-caps">Barcode / SSCC</label>
        <div className="flex gap-2 mt-1">
          <input className="hos-input flex-1" placeholder="Scan or enter" value={p.barcode || ""}
                 onChange={(e) => onChange(idx, { barcode: e.target.value })}
                 data-testid={`barcode-input-${p.pallet_number}`}/>
          <button type="button" className="hos-btn-outline flex items-center gap-1 text-xs"
                  onClick={() => setScanning(true)} data-testid={`scan-btn-${p.pallet_number}`}>
            <Barcode size={14}/> Scan
          </button>
        </div>
      </div>
      <div>
        <label className="label-caps">Notes</label>
        <textarea rows={2} className="hos-input mt-1" value={p.notes || ""}
                  onChange={(e) => onChange(idx, { notes: e.target.value })} placeholder="Optional"/>
      </div>
      <div>
        <label className="label-caps">Pallet Photos ({(p.photos || []).length})</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {(p.photos || []).map((ph, i) => (
            <div key={i} className="relative">
              <AuthImage photo={ph} className="w-16 h-16 object-cover rounded" alt="pallet"/>
              <button onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 bg-[color:var(--error)] rounded-full p-0.5"><X size={10}/></button>
            </div>
          ))}
          <label className="hos-btn-outline flex items-center gap-1 text-xs cursor-pointer">
            <Camera size={14}/> {busy ? "Uploading…" : "Photo"}
            <input type="file" accept="image/*" capture="environment" className="hidden"
                   onChange={(e) => takePhoto(e.target.files)}/>
          </label>
        </div>
      </div>
      {scanning && (
        <BarcodeScanner
          testId={`pallet-scan-${p.pallet_number}`}
          onDetected={(value) => {
            onChange(idx, { barcode: value });
            setScanning(false);
            toast.success(`Barcode captured for Pallet ${p.pallet_number}`);
          }}
          onClose={() => setScanning(false)}
        />
      )}
    </div>
  );
}

/* ---------- Step 3: Delivery Note ---------- */
function DeliveryNoteStep({ rec, patch }) {
  const [busy, setBusy] = useState(false);
  const photos = rec.delivery_note_photos || [];
  const addPhotos = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const added = [];
      for (const f of Array.from(files)) {
        const { id, url } = await uploadCompressedPhoto(f);
        added.push({ id, file_id: id, url });
      }
      await patch({ delivery_note_photos: [...photos, ...added] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const removePhoto = async (i) => {
    await patch({ delivery_note_photos: photos.filter((_, j) => j !== i) });
  };

  return (
    <div className="hos-card p-6 space-y-4">
      <div>
        <div className="label-caps">Delivery Note</div>
        <div className="font-heading text-2xl">Photograph the delivery note</div>
        <div className="text-sm text-[color:var(--text-secondary)] mt-1">Required for acceptance. Add multiple pages if needed. Photos are auto-compressed to ≤1 MB.</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-[color:var(--border)]">
            <AuthImage photo={p} className="w-full h-full object-cover" alt="delivery note"/>
            <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-[color:var(--error)] rounded-full p-1"><X size={12}/></button>
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5">Page {i + 1}</div>
          </div>
        ))}
        <label className="aspect-square rounded-md border-2 border-dashed border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] flex flex-col items-center justify-center text-[color:var(--gold)] cursor-pointer" data-testid="delivery-note-add">
          <Camera size={28}/>
          <span className="text-xs mt-2">{busy ? "Uploading…" : "Take Photo"}</span>
          <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                 onChange={(e) => addPhotos(e.target.files)}/>
        </label>
        <label className="aspect-square rounded-md border-2 border-dashed border-[color:var(--border)] hover:border-[color:var(--gold)] flex flex-col items-center justify-center text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] cursor-pointer">
          <Upload size={28}/>
          <span className="text-xs mt-2">Upload</span>
          <input type="file" accept="image/*" multiple className="hidden"
                 onChange={(e) => addPhotos(e.target.files)}/>
        </label>
      </div>

      <div>
        <label className="label-caps">General Shipment Notes</label>
        <textarea rows={3} className="hos-input mt-1" value={rec.general_notes || ""}
                  onChange={(e) => { patch({ general_notes: e.target.value }); }}/>
      </div>
    </div>
  );
}

/* ---------- Step 4: Issues ---------- */
function IssuesStep({ rec, setRec, patch }) {
  const issues = rec.issues || [];
  const pallets = rec.pallets || [];
  const [busy, setBusy] = useState(false);

  const save = (arr) => patch({ issues: arr });
  const addIssue = () => save([...issues, { issue_type: "damaged_boxes", description: "", quantity_affected: 0, pallet_id: null, photos: [] }]);
  const removeIssue = (i) => save(issues.filter((_, j) => j !== i));
  const updateIssue = (i, p) => save(issues.map((it, j) => j === i ? { ...it, ...p } : it));

  const addPhoto = async (i, files) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { id, url } = await uploadCompressedPhoto(file);
      const it = issues[i];
      updateIssue(i, { photos: [...(it.photos || []), { id, file_id: id, url }] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="hos-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps">Delivery Issues</div>
            <div className="text-sm text-[color:var(--text-secondary)]">{issues.length === 0 ? "No issues recorded. Great!" : `${issues.length} issue${issues.length === 1 ? "" : "s"} recorded`}</div>
          </div>
          <button className="hos-btn-primary flex items-center gap-1 text-sm" onClick={addIssue} data-testid="add-issue-btn"><Plus size={14}/> Add Issue</button>
        </div>
      </div>

      {issues.map((it, i) => (
        <div key={it.id || i} className="hos-card p-4 space-y-3" data-testid={`issue-card-${i}`}>
          <div className="flex items-center justify-between">
            <div className="font-heading text-lg">Issue {i + 1}</div>
            <button className="text-[color:var(--error)] p-1" onClick={() => removeIssue(i)}><Trash2 size={14}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-caps">Type</label>
              <select className="hos-input mt-1" value={it.issue_type} onChange={(e) => updateIssue(i, { issue_type: e.target.value })} data-testid={`issue-type-${i}`}>
                {ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">Related Pallet</label>
              <select className="hos-input mt-1" value={it.pallet_id || ""} onChange={(e) => updateIssue(i, { pallet_id: e.target.value || null })}>
                <option value="">— none —</option>
                {pallets.map((p) => <option key={p.id} value={p.id}>Pallet {p.pallet_number}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">Quantity Affected</label>
              <input type="number" min="0" inputMode="numeric" className="hos-input mt-1" value={it.quantity_affected || 0}
                     onChange={(e) => updateIssue(i, { quantity_affected: Math.max(0, Number(e.target.value) || 0) })}/>
            </div>
            <div>
              <label className="label-caps">Photos ({(it.photos || []).length})</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(it.photos || []).map((ph, pi) => (
                  <div key={pi} className="relative">
                    <AuthImage photo={ph} className="w-14 h-14 object-cover rounded" alt="issue"/>
                    <button onClick={() => updateIssue(i, { photos: it.photos.filter((_, jj) => jj !== pi) })} className="absolute -top-1 -right-1 bg-[color:var(--error)] rounded-full p-0.5"><X size={10}/></button>
                  </div>
                ))}
                <label className="hos-btn-outline flex items-center gap-1 text-xs cursor-pointer">
                  <Camera size={12}/> {busy ? "…" : "Add"}
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                         onChange={(e) => addPhoto(i, e.target.files)}/>
                </label>
              </div>
            </div>
          </div>
          <div>
            <label className="label-caps">Description</label>
            <textarea rows={2} className="hos-input mt-1" value={it.description || ""}
                      placeholder="e.g. Pallet 3 had two boxes with crushed corners"
                      onChange={(e) => updateIssue(i, { description: e.target.value })}/>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Step 5: Review & Sign ---------- */
function ReviewStep({ rec, onSign, confirmSign, setConfirmSign, signing, user }) {
  const st = rec.shipment_type || "pallets";
  const showPallets = st === "pallets" || st === "pallets_and_boxes";
  const showLoose = st === "boxes" || st === "pallets_and_boxes";
  const boxesOnPallets = (rec.pallets || []).reduce((s, p) => s + (p.box_count || 0), 0);
  const loose = Number(rec.loose_boxes || 0);
  const totalBoxes = (showPallets ? boxesOnPallets : 0) + (showLoose ? loose : 0);
  const noteMissing = !(rec.delivery_note_photos || []).length;
  const noPallets = showPallets && !(rec.pallets || []).length;
  const noBoxes = st === "boxes" && loose <= 0;
  const noSupplier = !rec.supplier_name;
  const canSign = !noteMissing && !noPallets && !noBoxes && !noSupplier && confirmSign;

  return (
    <div className="space-y-4">
      <div className="hos-card p-6">
        <div className="label-caps">Review</div>
        <h2 className="font-heading text-2xl mb-4">Receiving Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Info label="Reference" value={rec.reference_number}/>
          <Info label="Store" value={rec.store_name}/>
          <Info label="Shipment Type" value={SHIPMENT_TYPES.find((t) => t.value === st)?.label || st}/>
          <Info label="Supplier" value={rec.supplier_name || "—"} warn={noSupplier}/>
          <Info label="Purchase Order" value={rec.purchase_order_number || "—"}/>
          <Info label="Invoice" value={rec.invoice_number || "—"}/>
          <Info label="Arrival" value={rec.arrival_at ? new Date(rec.arrival_at).toLocaleString() : "—"}/>
          <Info label="Carrier" value={rec.carrier_name || "—"}/>
          <Info label="Driver" value={rec.driver_name || "—"}/>
          <Info label="Driver Phone" value={rec.driver_phone || "—"}/>
          <Info label="Received By" value={rec.received_by_name}/>
        </div>
      </div>

      <div className="hos-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading text-xl">Received Inventory</h3>
          <div className="text-right">
            <div className="label-caps">Total Boxes</div>
            <div className="font-heading text-3xl text-[color:var(--gold)]">{totalBoxes}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {showPallets && <MiniStat label="Pallets" value={rec.pallets?.length || 0} accent/>}
          {showPallets && <MiniStat label="Boxes on Pallets" value={boxesOnPallets}/>}
          {showLoose && <MiniStat label="Loose Boxes" value={loose}/>}
          <MiniStat label="Total Boxes" value={totalBoxes} accent/>
        </div>
        {noBoxes && <div className="text-[color:var(--error)] text-sm">Enter number of boxes received</div>}
        {noPallets && <div className="text-[color:var(--error)] text-sm">No pallets added</div>}
        {showPallets && (rec.pallets || []).length > 0 && (
          <ul className="divide-y divide-[color:var(--border)]">
            {rec.pallets.map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold">Pallet {p.pallet_number}</div>
                  <div className="text-xs text-[color:var(--text-secondary)]">{CONDITIONS.find((c) => c.value === p.condition)?.label} · {(p.photos || []).length} photo(s){p.barcode ? ` · ⌗ ${p.barcode}` : ""}</div>
                </div>
                <div className="font-heading text-lg">{p.box_count}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(rec.issues || []).length > 0 && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Issues ({rec.issues.length})</h3>
          <ul className="divide-y divide-[color:var(--border)]">
            {rec.issues.map((it) => {
              const pallet = (rec.pallets || []).find((p) => p.id === it.pallet_id);
              return (
                <li key={it.id} className="py-2 text-sm">
                  <div className="font-semibold">{ISSUE_TYPES.find((t) => t.value === it.issue_type)?.label} {pallet ? `· Pallet ${pallet.pallet_number}` : ""} {it.quantity_affected ? `· ${it.quantity_affected} affected` : ""}</div>
                  {it.description && <div className="text-xs text-[color:var(--text-secondary)]">{it.description}</div>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="hos-card p-6">
        <h3 className="font-heading text-xl mb-2">Documentation</h3>
        <div className={`text-sm ${noteMissing ? "text-[color:var(--warning)]" : "text-emerald-300"}`}>
          {noteMissing ? <><AlertTriangle className="inline" size={14}/> Delivery note photo required</> : <><CheckCircle2 className="inline" size={14}/> Delivery Note attached ({(rec.delivery_note_photos || []).length} page{(rec.delivery_note_photos || []).length === 1 ? "" : "s"})</>}
        </div>
      </div>

      <div className="hos-card p-6 space-y-3">
        <h3 className="font-heading text-xl">Inventory Receiving Confirmation</h3>
        <p className="text-sm text-[color:var(--text-secondary)]">
          I confirm that I have physically checked the pallets and/or boxes received, verified the counts recorded above, documented any visible issues, and accept the shipment based on this receiving record.
        </p>
        <div className="text-sm">
          <div className="label-caps">Inventory Manager</div>
          <div className="font-heading text-lg">{rec.received_by_name} <span className="text-xs text-[color:var(--text-secondary)]">({user?.email})</span></div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={confirmSign} onChange={(e) => setConfirmSign(e.target.checked)} data-testid="confirm-sign-checkbox"/>
          I confirm and accept this shipment.
        </label>
        <button onClick={onSign} disabled={!canSign || signing}
                className="hos-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="sign-accept-btn">
          <PenLine size={16}/> {signing ? "Signing…" : "Sign & Accept Inventory"}
        </button>
      </div>
    </div>
  );
}

function Info({ label, value, warn }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className={`font-semibold ${warn ? "text-[color:var(--warning)]" : ""}`}>{value}</div>
    </div>
  );
}
